import React, { ReactNode, useCallback, useState } from "react";

import { ChevronLeft } from "icons";
import { color } from "minimum-ui/tokens";
import styled, { css } from "styled-components";

import Cell from "./Cell";
import { HeadCellWrapper } from "./HeadCell";
import Table from "./Table";

export interface ExpandableRowProps {
  columns: (string | number)[];
  contents: ReactNode;
}

const ExpandableRow: React.FC<ExpandableRowProps> = ({
  columns,
  contents,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const onToggle = useCallback(() => setOpen((o) => !o), []);
  const content = React.Children.toArray(contents);
  return (
    <>
      {content.map((item, index) => (
        <ExpandableRowWrapper key={index} $first={index === 0}>
          {index === 0 && (
            <Indicator
              $open={open}
              onClick={onToggle}
              aria-label={open ? "Collapse" : "Expand"}
            >
              <ChevronLeft />
            </Indicator>
          )}
          {item}
        </ExpandableRowWrapper>
      ))}
      {open && <Content columns={columns}>{children}</Content>}
    </>
  );
};

const Indicator = styled.div<{ $open: boolean }>`
  position: absolute;
  cursor: pointer;
  width: 24px;
  height: 24px;
  border: 0;
  padding: 0;
  margin: 0;
  left: 12px;
  top: 50%;
  margin-top: -12px;
  overflow: hidden;
  font-size: 10px;
  line-height: 24px;
  transform: rotate(0deg);
  transition: 0.4s ease;

  ${(props) =>
    props.$open &&
    css`
      transform: rotate(180deg);
    `}

  svg {
    width: 24px;
    height: 24px;
    color: ${color.blue[300]};
    transform: rotate(-90deg);
  }
`;

const ExpandableRowWrapper = styled.div<{ $first: boolean }>`
  position: relative;
  background: ${color.neutral[100]};

  --header-color: ${color.neutral[100]};

  ${(props) =>
    props.$first &&
    css`
      // Allocate space for the expand-toggle
      padding-left: 60px;
      ${Cell} {
        padding-left: 0;
      }
    `}
  ${Cell} {
    color: ${color.neutral[500]};
    border-bottom: 0;
    height: 100%;
  }

  border-top: 1px solid ${color.neutral[200]};
  border-bottom: 1px solid ${color.neutral[200]};
  &:first-of-type {
    border-top: 0;
  }
`;

const Content = styled(Table)`
  grid-column: 1 / -1;
  border-bottom: 0;

  ${HeadCellWrapper} {
    background: ${color.basic.white};
    border-top: 0;
  }
`;

export default ExpandableRow;
